import { metric, log as graylog } from '@s4l/monitoring'
import { useS4LContext } from '@s4l/react-context'
import type { UseCaseEvent } from '@s4l/stayforlong'
import { useEffect, useRef, useCallback, useState } from 'react'
import { ulid } from 'ulid'
import debug from 'debug'

const log = debug('s4l:uikit:SearchComparatorLogger')

export interface LegacyProperty {
  id: number | string
}

export interface SearchComparisonData {
  countsMatch: boolean
  propertyIdsMatch: boolean
  samePropertiesDifferentOrder: boolean
  fullMatch: boolean
  legacyCount: number
  newUseCaseCount: number
  legacyTotalResults: number
  newUseCaseTotalResults: number
  countDifference: number
  missingInNewCount: number
  extraInNewCount: number
  legacyFirstIds: string
  newUseCaseFirstIds: string
  timestamp: string
}

interface NewUseCaseRequestParams {
  search: unknown
  criteria: unknown
  lang: unknown
  market: unknown
  affiliate: unknown
  source: unknown
}

// Normalized format for comparing legacy and new use case requests
interface NormalizedRequestPayload {
  location: { type?: string; slug?: string; country?: string }
  dateRange: { checkIn?: string; checkOut?: string }
  rooms?: unknown[]
  page?: number
  pageSize?: number
  sortBy?: string
  filters: {
    stars?: unknown[]
    rating?: unknown
    isFamiliar?: boolean
    accommodations?: unknown[]
    facilities?: unknown[]
    minPrice?: number
    maxPrice?: number
    themes?: unknown[]
    boards?: unknown[]
    paymentOptions?: unknown[]
    cancellationPolicies?: unknown[]
  }
  affiliate?: string
  source?: string
  market?: string
  lang?: string
  currency?: string
  priceType?: string
  isLogged?: boolean
  highlightedProperty?: string | number
  minSellingRate?: number | string
}

// Represents a single field difference between legacy and new use case
interface DiffEntry {
  legacy: unknown
  newUseCase: unknown
}

// Fields to ignore in the diff - these are derived at repository level and will always match
const IGNORED_FIELDS = new Set([
  'currency', // Derived from market in HTTPPropertyRepository
  'priceType', // Derived from affiliate + market in HTTPPropertyRepository
  'price_type', // snake_case version
  'isLogged', // Not relevant for request comparison
  'country', // Duplicated in location
])

// Normalize a value for comparison - treats "empty" values as equivalent
// Empty arrays [], undefined, null, and false are all considered "no value"
export function normalizeForComparison(value: unknown): string {
  // Treat undefined, null as empty
  if (value === undefined || value === null) return ''
  // Treat false as empty (for boolean filters like isFamiliar)
  if (value === false) return ''
  // Treat empty arrays as empty
  if (Array.isArray(value) && value.length === 0) return ''
  // For objects, recursively normalize
  if (typeof value === 'object' && !Array.isArray(value)) {
    const normalized: Record<string, unknown> = {}
    let hasNonEmptyValue = false
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      const normalizedV = normalizeForComparison(v)
      if (normalizedV !== '') {
        normalized[k] = v
        hasNonEmptyValue = true
      }
    })
    // If all values in the object are empty, treat the whole object as empty
    if (!hasNonEmptyValue) return ''
    return JSON.stringify(normalized)
  }
  return JSON.stringify(value)
}

// Simple deep diff - compare two objects and return all differences
export function deepDiff(
  legacy: Record<string, unknown> | undefined,
  newUseCase: Record<string, unknown> | undefined
): Record<string, DiffEntry> {
  const diff: Record<string, DiffEntry> = {}

  if (!legacy || !newUseCase) {
    return { _error: { legacy: legacy ?? null, newUseCase: newUseCase ?? null } }
  }

  // Get all unique keys from both objects
  const allKeys = new Set([...Object.keys(legacy), ...Object.keys(newUseCase)])

  allKeys.forEach(key => {
    // Skip ignored fields
    if (IGNORED_FIELDS.has(key)) return

    const legacyValue = legacy[key]
    const newUseCaseValue = newUseCase[key]

    // Normalize values for comparison (empty arrays, undefined, false are equivalent)
    const legacyNormalized = normalizeForComparison(legacyValue)
    const newUseCaseNormalized = normalizeForComparison(newUseCaseValue)

    if (legacyNormalized !== newUseCaseNormalized) {
      diff[key] = {
        legacy: legacyValue ?? null,
        newUseCase: newUseCaseValue ?? null,
      }
    }
  })

  return diff
}

export interface SearchComparatorLoggerProps {
  legacyProperties: LegacyProperty[]
  legacyTotalResults: number
  isLegacyFetching: boolean
  currentPage: number
  /**
   * Optional: The exact request payload sent by the legacy Redux search.
   * If provided, this will be logged to Graylog for comparison with the new use case request.
   * This should be the result of buildSearchPropertiesPayload() from the legacy code.
   */
  legacyRequestPayload?: Record<string, unknown>
}

interface ComparisonResult {
  countsMatch: boolean
  propertyIdsMatch: boolean
  orderMatch: boolean
  legacy: {
    count: number
    propertyIds: string[]
  }
  newUseCase: {
    count: number
    propertyIds: string[]
  }
  differences: {
    missingInNew: string[]
    extraInNew: string[]
    samePropertiesDifferentOrder: boolean
  }
}

interface NewSearchResultJSON {
  properties: {
    list: Array<{ id: string; [key: string]: unknown }>
  }
  metadata: {
    count: number
    price: { min: number; max: number }
    [key: string]: unknown
  }
}

function compareResults(
  legacyProperties: LegacyProperty[],
  newSearchResultJSON: NewSearchResultJSON
): ComparisonResult {
  const legacyPropertyIds = legacyProperties.map(p => String(p.id))
  const newPropertiesJSON = newSearchResultJSON.properties
  const newPropertyIds = newPropertiesJSON.list.map(p => String(p.id))

  const countsMatch = legacyProperties.length === newPropertiesJSON.list.length
  const propertyIdsMatch = JSON.stringify(legacyPropertyIds) === JSON.stringify(newPropertyIds)

  const sortedLegacy = [...legacyPropertyIds].sort((a, b) => a.localeCompare(b))
  const sortedNew = [...newPropertyIds].sort((a, b) => a.localeCompare(b))
  const samePropertiesDifferentOrder =
    !propertyIdsMatch && JSON.stringify(sortedLegacy) === JSON.stringify(sortedNew)

  const missingInNew = legacyPropertyIds.filter(id => !newPropertyIds.includes(id))
  const extraInNew = newPropertyIds.filter(id => !legacyPropertyIds.includes(id))

  return {
    countsMatch,
    propertyIdsMatch,
    orderMatch: propertyIdsMatch,
    legacy: {
      count: legacyProperties.length,
      propertyIds: legacyPropertyIds,
    },
    newUseCase: {
      count: newPropertiesJSON.list.length,
      propertyIds: newPropertyIds,
    },
    differences: {
      missingInNew,
      extraInNew,
      samePropertiesDifferentOrder,
    },
  }
}

function buildComparisonData(
  comparison: ComparisonResult,
  legacyTotalResults: number,
  newSearchResultJSON: NewSearchResultJSON
): SearchComparisonData {
  return {
    countsMatch: comparison.countsMatch,
    propertyIdsMatch: comparison.propertyIdsMatch,
    samePropertiesDifferentOrder: comparison.differences.samePropertiesDifferentOrder,
    fullMatch: comparison.countsMatch && comparison.propertyIdsMatch,
    legacyCount: comparison.legacy.count,
    newUseCaseCount: comparison.newUseCase.count,
    legacyTotalResults,
    newUseCaseTotalResults: newSearchResultJSON.metadata.count,
    countDifference: Math.abs(comparison.legacy.count - comparison.newUseCase.count),
    missingInNewCount: comparison.differences.missingInNew.length,
    extraInNewCount: comparison.differences.extraInNew.length,
    legacyFirstIds: comparison.legacy.propertyIds.slice(0, 5).join(','),
    newUseCaseFirstIds: comparison.newUseCase.propertyIds.slice(0, 5).join(','),
    timestamp: new Date().toISOString(),
  }
}

function logComparisonResults(
  comparisonData: SearchComparisonData,
  comparison: ComparisonResult
): void {
  const fullMatchIcon = comparisonData.fullMatch ? '✅' : '❌'
  const countsMatchIcon = comparisonData.countsMatch ? '✅' : '❌'
  const idsMatchIcon = comparisonData.propertyIdsMatch ? '✅' : '❌'
  const orderIcon = comparisonData.samePropertiesDifferentOrder ? '🔀' : '➖'

  log(`
╔══════════════════════════════════════════════════════════════╗
║           🔍 SEARCH COMPARATOR RESULTS                       ║
╠══════════════════════════════════════════════════════════════╣
║  ${fullMatchIcon} Full Match: ${comparisonData.fullMatch ? 'YES' : 'NO'}
║  ${countsMatchIcon} Counts Match: ${comparisonData.countsMatch ? 'YES' : 'NO'}
║  ${idsMatchIcon} Property IDs Match: ${comparisonData.propertyIdsMatch ? 'YES' : 'NO'}
║  ${orderIcon} Same Properties Different Order: ${comparisonData.samePropertiesDifferentOrder ? 'YES' : 'NO'}
╠══════════════════════════════════════════════════════════════╣
║  📊 COUNTS                                                   ║
║  ├─ Legacy Count: ${comparisonData.legacyCount}
║  ├─ New Use Case Count: ${comparisonData.newUseCaseCount}
║  ├─ Legacy Total Results: ${comparisonData.legacyTotalResults}
║  ├─ New Use Case Total Results: ${comparisonData.newUseCaseTotalResults}
║  └─ Difference: ${comparisonData.countDifference === 0 ? '✅ 0' : `⚠️ ${comparisonData.countDifference}`}
╠══════════════════════════════════════════════════════════════╣
║  🏨 PROPERTY IDS (first 5)                                   ║
║  ├─ Legacy: [${comparisonData.legacyFirstIds || 'none'}]
║  └─ New:    [${comparisonData.newUseCaseFirstIds || 'none'}]
╠══════════════════════════════════════════════════════════════╣
║  📋 DIFFERENCES                                              ║
║  ├─ Missing in New: ${comparisonData.missingInNewCount === 0 ? '✅ 0' : `❌ ${comparisonData.missingInNewCount}`}
║  └─ Extra in New: ${comparisonData.extraInNewCount === 0 ? '✅ 0' : `⚠️ ${comparisonData.extraInNewCount}`}
╚══════════════════════════════════════════════════════════════╝
      `)

  if (comparison.differences.missingInNew.length > 0) {
    log(`❌ Missing in New: [${comparison.differences.missingInNew.join(', ')}]`)
  }
  if (comparison.differences.extraInNew.length > 0) {
    log(`⚠️ Extra in New: [${comparison.differences.extraInNew.join(', ')}]`)
  }
}

/**
 * SearchComparatorLogger
 *
 * Temporary component for migration validation. Subscribes to new DDD use case search events
 * and compares results with legacy Redux search to ensure parity. Logs differences to console
 * and sends metrics to DataDog/Graylog for monitoring.
 */
export const SearchComparatorLogger = ({
  legacyProperties,
  legacyTotalResults,
  isLegacyFetching,
  currentPage,
  legacyRequestPayload,
}: SearchComparatorLoggerProps) => {
  const { domain } = useS4LContext()

  // Store request parameters from sub-use-case events
  const requestParamsRef = useRef<NewUseCaseRequestParams | null>(null)

  // Store new use case result until legacy search completes (using state to trigger effects)
  const [pendingComparison, setPendingComparison] = useState<{
    resultJSON: NewSearchResultJSON
    normalizedRequest: NormalizedRequestPayload
  } | null>(null)

  const processComparison = useCallback(
    (resultJSON: NewSearchResultJSON, normalizedNewUseCaseRequest: NormalizedRequestPayload) => {
      // For pagination, legacy accumulates all properties but new use case returns only current page
      // Slice legacy to compare only the current page's properties
      const PROPERTIES_PER_PAGE = 18
      const startIndex = (currentPage - 1) * PROPERTIES_PER_PAGE
      const currentPageLegacyProperties = legacyProperties.slice(
        startIndex,
        startIndex + PROPERTIES_PER_PAGE
      )

      const comparison = compareResults(currentPageLegacyProperties, resultJSON)
      const comparisonData = buildComparisonData(comparison, legacyTotalResults, resultJSON)

      log(
        'Comparing page %d: legacy properties [%d-%d] vs new use case',
        currentPage,
        startIndex,
        startIndex + currentPageLegacyProperties.length - 1
      )

      logComparisonResults(comparisonData, comparison)

      metric.send({
        id: ulid(),
        name: 'SearchComparatorLogger',
        metadata: {
          match: comparisonData.fullMatch,
        },
      })

      // Send request param differences to Graylog only when there's no full match
      if (!comparisonData.fullMatch) {
        const requestDiff = deepDiff(
          legacyRequestPayload,
          normalizedNewUseCaseRequest as unknown as Record<string, unknown>
        )

        // Only send to graylog if there are actual differences
        if (Object.keys(requestDiff).length > 0) {
          graylog.info({
            message: 'SearchComparatorLogger: Request differences',
            metadata: {
              diff: requestDiff,
            },
          })
        }

        log('Request diff:', requestDiff)
      }
    },
    [legacyProperties, legacyTotalResults, currentPage, legacyRequestPayload]
  )

  // Helper to safely serialize domain models
  const serialize = useCallback((value: unknown): unknown => {
    if (value && typeof value === 'object' && 'toJSON' in value) {
      return (value as { toJSON: () => unknown }).toJSON()
    }
    return value
  }, [])

  // Build normalized request from captured params
  const buildNormalizedRequest = useCallback(
    (requestParams: NewUseCaseRequestParams): NormalizedRequestPayload => {
      const searchData = requestParams.search as {
        location?: { type?: string; slug?: string; country?: string }
        partialSearch?: { checkIn?: string; checkOut?: string; paxes?: unknown[] }
      }
      const criteriaData = requestParams.criteria as {
        pagination?: { page?: number; pageSize?: number }
        sort?: { value?: string }
        filters?: {
          stars?: { list?: unknown[] }
          ratings?: { list?: unknown[] }
          facilities?: { list?: unknown[] }
          accommodationTypes?: { list?: unknown[] }
          themes?: { list?: unknown[] }
          boards?: { list?: unknown[] }
          paymentOptions?: { list?: unknown[] }
          cancellationPolicies?: { list?: unknown[] }
          priceRange?: { min?: number; max?: number }
          familyFriendly?: boolean
        }
      }

      return {
        location: {
          type: searchData?.location?.type,
          slug: searchData?.location?.slug,
          country: searchData?.location?.country,
        },
        dateRange: {
          checkIn: searchData?.partialSearch?.checkIn,
          checkOut: searchData?.partialSearch?.checkOut,
        },
        rooms: searchData?.partialSearch?.paxes,
        page: criteriaData?.pagination?.page,
        pageSize: criteriaData?.pagination?.pageSize,
        sortBy: criteriaData?.sort?.value,
        filters: {
          stars: criteriaData?.filters?.stars?.list,
          rating: criteriaData?.filters?.ratings?.list?.[0],
          isFamiliar: criteriaData?.filters?.familyFriendly,
          accommodations: criteriaData?.filters?.accommodationTypes?.list,
          facilities: criteriaData?.filters?.facilities?.list,
          minPrice: criteriaData?.filters?.priceRange?.min,
          maxPrice: criteriaData?.filters?.priceRange?.max,
          themes: criteriaData?.filters?.themes?.list,
          boards: criteriaData?.filters?.boards?.list,
          paymentOptions: criteriaData?.filters?.paymentOptions?.list,
          cancellationPolicies: criteriaData?.filters?.cancellationPolicies?.list,
        },
        affiliate:
          typeof requestParams.affiliate === 'string'
            ? requestParams.affiliate
            : (requestParams.affiliate as { value?: string })?.value,
        source:
          typeof requestParams.source === 'string'
            ? requestParams.source
            : (requestParams.source as { value?: string })?.value,
        market:
          typeof requestParams.market === 'string'
            ? requestParams.market
            : (requestParams.market as { value?: string })?.value,
        lang:
          typeof requestParams.lang === 'string'
            ? requestParams.lang
            : (requestParams.lang as { value?: string })?.value,
        highlightedProperty: (() => {
          const value = (
            requestParams.criteria as { highlightedProperty?: { value?: string | number } }
          )?.highlightedProperty?.value
          if (typeof value === 'string') return Number(value) || undefined
          return value
        })(),
      }
    },
    []
  )

  // Subscribe to sub-use-case events to capture request parameters
  useEffect(() => {
    const onSearchCreated = ({ response }: UseCaseEvent<'CreateSearchPropertyUseCase'>) => {
      const [error, search] = response
      if (!error && search) {
        requestParamsRef.current = {
          ...requestParamsRef.current,
          search: serialize(search),
        } as NewUseCaseRequestParams
      }
    }

    const onCriteriaCreated = ({
      response,
    }: UseCaseEvent<'CreateSearchCriteriaPropertyUseCase'>) => {
      const [error, criteria] = response
      if (!error && criteria) {
        requestParamsRef.current = {
          ...requestParamsRef.current,
          criteria: serialize(criteria),
        } as NewUseCaseRequestParams
      }
    }

    const onLanguageRetrieved = ({ response }: UseCaseEvent<'GetCurrentLanguageSharedUseCase'>) => {
      const [error, lang] = response
      if (!error && lang) {
        requestParamsRef.current = {
          ...requestParamsRef.current,
          lang: serialize(lang),
        } as NewUseCaseRequestParams
      }
    }

    const onMarketRetrieved = ({ response }: UseCaseEvent<'GetCurrentMarketSharedUseCase'>) => {
      const [error, market] = response
      if (!error && market) {
        requestParamsRef.current = {
          ...requestParamsRef.current,
          market: serialize(market),
        } as NewUseCaseRequestParams
      }
    }

    const onAffiliateRetrieved = ({
      response,
    }: UseCaseEvent<'GetCurrentAffiliateSharedUseCase'>) => {
      const [error, affiliate] = response
      if (!error && affiliate) {
        requestParamsRef.current = {
          ...requestParamsRef.current,
          affiliate: serialize(affiliate),
        } as NewUseCaseRequestParams
      }
    }

    const onSourceRetrieved = ({ response }: UseCaseEvent<'GetCurrentSourceSharedUseCase'>) => {
      const [error, source] = response
      if (!error && source) {
        requestParamsRef.current = {
          ...requestParamsRef.current,
          source: serialize(source),
        } as NewUseCaseRequestParams
      }
    }

    domain.on('CreateSearchPropertyUseCase', onSearchCreated)
    domain.on('CreateSearchCriteriaPropertyUseCase', onCriteriaCreated)
    domain.on('GetCurrentLanguageSharedUseCase', onLanguageRetrieved)
    domain.on('GetCurrentMarketSharedUseCase', onMarketRetrieved)
    domain.on('GetCurrentAffiliateSharedUseCase', onAffiliateRetrieved)
    domain.on('GetCurrentSourceSharedUseCase', onSourceRetrieved)

    return () => {
      domain.off('CreateSearchPropertyUseCase', onSearchCreated)
      domain.off('CreateSearchCriteriaPropertyUseCase', onCriteriaCreated)
      domain.off('GetCurrentLanguageSharedUseCase', onLanguageRetrieved)
      domain.off('GetCurrentMarketSharedUseCase', onMarketRetrieved)
      domain.off('GetCurrentAffiliateSharedUseCase', onAffiliateRetrieved)
      domain.off('GetCurrentSourceSharedUseCase', onSourceRetrieved)
    }
  }, [domain, serialize])

  // Subscribe to the main search use case completion event
  useEffect(() => {
    const onSearchCompleted = ({
      response,
    }: UseCaseEvent<'SearchFromPartialAndCriteriaPropertyUseCase'>) => {
      const [error, result] = response

      if (error || !result) {
        log('Error in new use case search:', error)
        return
      }

      // Only process comparison if we have captured request params
      if (!requestParamsRef.current) {
        log('Request params not captured yet, skipping comparison')
        return
      }

      log('New use case search completed, storing result for comparison...')

      const resultJSON = result.toJSON() as NewSearchResultJSON
      const normalizedNewUseCaseRequest = buildNormalizedRequest(requestParamsRef.current)

      // Store the result to compare when legacy search completes
      setPendingComparison({
        resultJSON,
        normalizedRequest: normalizedNewUseCaseRequest,
      })
    }

    domain.on('SearchFromPartialAndCriteriaPropertyUseCase', onSearchCompleted)

    return () => {
      domain.off('SearchFromPartialAndCriteriaPropertyUseCase', onSearchCompleted)
    }
  }, [domain, buildNormalizedRequest])

  // Process comparison when both conditions are met:
  // 1. We have a pending comparison result from new use case
  // 2. Legacy search is not fetching (completed)
  useEffect(() => {
    if (pendingComparison && !isLegacyFetching) {
      log('Both searches completed, processing comparison...')
      const { resultJSON, normalizedRequest } = pendingComparison
      processComparison(resultJSON, normalizedRequest)
      setPendingComparison(null)
    }
  }, [pendingComparison, isLegacyFetching, processComparison])

  return null
}

SearchComparatorLogger.displayName = 'SearchComparatorLogger'
export default SearchComparatorLogger
